import type { Identifier } from "ra-core";

import { cn } from "@/lib/utils";

import { phoneChatUrl } from "../whatsapp/whatsapp";
import { savePendingCall } from "./pendingCall";

/** WhatsApp glyph (lucide has none). */
export const WhatsappIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2Zm5.8 14.03c-.25.69-1.44 1.32-1.98 1.37-.51.05-.99.24-3.33-.69-2.81-1.11-4.6-3.99-4.74-4.17-.14-.18-1.13-1.5-1.13-2.87 0-1.37.72-2.04.97-2.32.25-.28.55-.35.74-.35l.53.01c.17.01.4-.06.62.48.25.6.85 2.07.92 2.22.07.15.12.32.02.51-.1.19-.15.32-.29.49-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.03 1.12 1 2.07 1.31 2.36 1.46.29.15.46.12.63-.07.17-.19.72-.84.92-1.13.19-.29.38-.24.64-.15.27.1 1.7.8 1.99.95.29.15.48.22.55.34.07.12.07.69-.18 1.38Z" />
  </svg>
);

/**
 * Opens WhatsApp Business with this client to call them, and leaves a note
 * so the CRM asks "¿Cómo ha ido?" when the setter comes back.
 */
export const WhatsappCallButton = ({
  clientId,
  name,
  phone,
  variant = "big",
  className,
}: {
  clientId: Identifier;
  name: string;
  phone?: string | null;
  variant?: "big" | "icon" | "pill";
  className?: string;
}) => {
  if (!phone) return null;
  const start = () => savePendingCall(clientId, name);
  const common = {
    href: phoneChatUrl(phone),
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: start,
  };
  if (variant === "icon") {
    return (
      <a
        {...common}
        className={cn(
          "rounded-full p-2 text-[#128C7E] hover:bg-white dark:text-[#25D366] dark:hover:bg-neutral-800",
          className,
        )}
        title={`Llamar a ${name} por WhatsApp`}
        aria-label={`Llamar a ${name} por WhatsApp`}
      >
        <WhatsappIcon className="h-5 w-5" />
      </a>
    );
  }
  if (variant === "pill") {
    return (
      <a
        {...common}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-sm font-semibold text-neutral-900",
          className,
        )}
      >
        <WhatsappIcon className="h-4 w-4" />
        Llamar por WhatsApp
      </a>
    );
  }
  return (
    <a
      {...common}
      className={cn(
        "flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] text-lg font-semibold text-neutral-900 shadow-sm active:scale-[0.98]",
        className,
      )}
    >
      <WhatsappIcon className="h-6 w-6" />
      Llamar por WhatsApp
    </a>
  );
};
